import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Calendar, MapPin, Clock, ArrowLeft, ExternalLink } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { GoldButton, Eyebrow } from "@/components/shared";
import { Ornament } from "@/components/Ornament";
import client, { imgUrl } from "@/lib/api";
import { eventsI18n, catLabel } from "@/i18n/events";

const formatDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

const EventDetailPage = () => {
  const { id } = useParams();
  const { lang } = useLang();
  const te = eventsI18n[lang];
  const ref = useReveal([lang, id]);
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    client
      .get(`/events/${id}`)
      .then((r) => setEv(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="font-serif text-2xl text-espresso">{te.empty}</p>
        <GoldButton as="button" onClick={() => navigate("/neuigkeiten")} data-testid="detail-back-empty">
          <ArrowLeft className="w-4 h-4" /> {te.detailBack}
        </GoldButton>
      </div>
    );
  }

  if (!ev) return <div className="min-h-[70vh] flex items-center justify-center text-espresso/40">…</div>;

  return (
    <div ref={ref} data-testid="event-detail">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        {ev.cover_image ? (
          <div className="absolute inset-0 scale-110" style={{ backgroundImage: `url(${imgUrl(ev.cover_image)})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        ) : (
          <div className="absolute inset-0 bg-espresso-deep" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-espresso-deep via-espresso-deep/60 to-espresso-deep/30" />
        <div className="absolute inset-0 grain-overlay" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 pb-14 pt-40 w-full">
          <Eyebrow dark>{catLabel(ev.category, lang)}</Eyebrow>
          <h1 className="font-serif text-cream text-3xl sm:text-4xl lg:text-5xl leading-[1.05] max-w-3xl text-shadow-warm">{ev.title}</h1>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24 bg-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <Link to="/neuigkeiten" data-testid="detail-back" className="cursor-link inline-flex items-center gap-2 text-sm text-espresso/60 hover:text-gold-deep transition-colors mb-10">
            <ArrowLeft className="w-4 h-4" /> {te.detailBack}
          </Link>

          {/* Meta */}
          {(ev.event_date || ev.location) && (
            <div className="flex flex-wrap gap-x-10 gap-y-4 mb-10 pb-10 border-b border-border">
              {ev.event_date && (
                <div>
                  <span className="block text-[11px] tracking-[0.3em] uppercase text-gold-deep mb-1">{te.when}</span>
                  <span className="inline-flex items-center gap-2 text-espresso font-sans">
                    <Calendar className="w-4 h-4 text-gold-deep" /> {formatDate(ev.event_date, lang)}
                    {ev.event_time && (<><Clock className="w-4 h-4 text-gold-deep ml-2" /> {ev.event_time}</>)}
                  </span>
                </div>
              )}
              {ev.location && (
                <div>
                  <span className="block text-[11px] tracking-[0.3em] uppercase text-gold-deep mb-1">{te.where}</span>
                  <span className="inline-flex items-center gap-2 text-espresso font-sans">
                    <MapPin className="w-4 h-4 text-gold-deep" /> {ev.location}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Content */}
          {ev.short_desc && <p className="font-serif text-xl text-espresso/85 leading-relaxed mb-6">{ev.short_desc}</p>}
          {ev.full_content && (
            <div className="prose-mara text-espresso/75 font-sans leading-relaxed space-y-4 whitespace-pre-line">{ev.full_content}</div>
          )}

          {/* Gallery */}
          {ev.gallery && ev.gallery.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-12" data-testid="detail-gallery">
              {ev.gallery.map((g, i) => (
                <div key={i} className="rounded-2xl overflow-hidden aspect-[4/3] bg-sand">
                  <img src={imgUrl(g)} alt="" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          {ev.link_url && ev.link_text && (
            <div className="mt-12">
              <GoldButton as="a" href={ev.link_url} target="_blank" rel="noreferrer" data-testid="detail-cta">
                {ev.link_text} <ExternalLink className="w-4 h-4" />
              </GoldButton>
            </div>
          )}

          <Ornament className="mt-16 opacity-60" width={200} />
        </div>
      </section>
    </div>
  );
};

export default EventDetailPage;
